import { useEffect, useState } from 'react';

import type {
  ClipSessionRequest,
  SdkEvent,
  VgpsClipSession,
  VgpsClipSessionState,
  VgpsLivekitWebRuntime,
  VgpsSession,
  VgpsSessionState,
} from '../core/runtime/types';
import { useRuntimeContext } from './provider';

export function useVgpsRuntime(): VgpsLivekitWebRuntime | null {
  return useRuntimeContext();
}

export function useVgpsSessionState(session: VgpsSession | null | undefined): VgpsSessionState {
  const [state, setState] = useState<VgpsSessionState>(session?.state ?? 'idle');

  useEffect(() => {
    if (!session) {
      setState('idle');
      return undefined;
    }

    return session.subscribeState((nextState) => {
      setState(nextState);
    });
  }, [session]);

  return state;
}

/** Subscribe to all SDK events from the runtime. */
export function useVgpsEvents(listener: (event: SdkEvent) => void): void {
  const runtime = useRuntimeContext();

  useEffect(() => {
    if (!runtime) return undefined;
    return runtime.subscribe(listener);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime]);
}

/**
 * useClipSession — open a clip-download session and track its state.
 * Returns `{ session, state }`. Call `session.requestPlayback(slotId, camera?)`
 * once status is 'ready' to start a clip transfer.
 */
export function useClipSession(req: { deviceSerial: string; channelId: string } | null): {
  session: VgpsClipSession | null;
  state: VgpsClipSessionState;
} {
  const runtime = useRuntimeContext();
  const [session, setSession] = useState<VgpsClipSession | null>(null);
  const [state, setState] = useState<VgpsClipSessionState>({
    status: 'connecting',
    error: null,
    videoUrl: null,
    progress: 0,
    durationMs: 0,
    slotId: null,
    buffering: false,
  });

  useEffect(() => {
    if (!runtime || !req?.deviceSerial || !req?.channelId) {
      setSession(null);
      return undefined;
    }
    let active = true;
    let s: VgpsClipSession | null = null;
    let unsub: (() => void) | null = null;

    (async () => {
      try {
        const opened = await runtime.openClipSession({
          deviceSerial: req.deviceSerial,
          channelId: req.channelId,
        } as ClipSessionRequest);
        if (!active) { opened.close().catch(() => {}); return; }
        s = opened;
        setSession(opened);
        unsub = opened.subscribeState((next) => { if (active) setState(next); });
      } catch (err) {
        if (active) setState((cur) => ({ ...cur, status: 'error', error: err instanceof Error ? err.message : String(err) }));
      }
    })();

    return () => {
      active = false;
      if (unsub) { try { unsub(); } catch { /* ignore */ } }
      if (s) { s.close().catch(() => {}); }
      setSession(null);
    };
  }, [runtime, req?.deviceSerial, req?.channelId]);

  return { session, state };
}

export type NetworkQuality = 'good' | 'degraded' | 'poor' | 'unknown';

/**
 * useQosState — tracks the network quality for a specific session.
 *
 * Subscribes to 'network_state' SDK events and returns the latest quality
 * classification. Returns 'unknown' until the first QoS poll fires (~3s
 * after the session starts playing).
 */
export function useQosState(session: VgpsSession | null | undefined): NetworkQuality {
  const runtime = useRuntimeContext();
  const [quality, setQuality] = useState<NetworkQuality>('unknown');

  useEffect(() => {
    if (!runtime || !session) {
      setQuality('unknown');
      return undefined;
    }

    return runtime.subscribe((event) => {
      if (
        event.type === 'network_state' &&
        event.sessionId === session.sessionId &&
        event.state !== 'recovering'
      ) {
        setQuality(event.state as NetworkQuality);
      }
    });
  }, [runtime, session]);

  return quality;
}
